import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { get } from '@/features/shared/lib/api';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

/**
 * ResearcherCountChart
 * - 연구자수 차트 컴포넌트
 * - ImpactFactorTrendChart와 동일한 구조와 모양
 * - API는 아직 구현 전이므로 externalSeries로 데이터를 받음
 */
export function ResearcherCountChart({
  series: externalSeries = null,
  height = 300,
  yMax: forcedYMax,
  hideXAxisLabels = false,
  showLegend = true,
  loading: externalLoading = null,
  error: externalError = null,
}) {
  // API에서 데이터 가져오기 (externalSeries가 없을 때만)
  // TODO: API 구현 후 활성화
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendErr, setTrendErr] = useState(null);

  useEffect(() => {
    // externalSeries가 제공되면 API 호출하지 않음
    if (externalSeries !== null) {
      return;
    }

    let alive = true;
    async function fetchTrend() {
      setTrendLoading(true);
      setTrendErr(null);
      try {
        // TODO: 연구자수 차트 API 엔드포인트 구현 후 활성화
        // const response = await get('/rm/v1/researcher/count_trend');
        const response = null;

        if (alive) {
          if (response?.status && response?.result) {
            setTrendData(response.result);
          } else {
            setTrendErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setTrendData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setTrendErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setTrendLoading(false);
        }
      }
    }
    fetchTrend();
    return () => { alive = false; };
  }, [externalSeries]);

  // 데이터 변환: API 데이터를 series 형식으로 변환
  const series = useMemo(() => {
    // externalSeries가 제공되면 그대로 사용
    if (externalSeries !== null && Array.isArray(externalSeries)) {
      return externalSeries;
    }

    // API 데이터가 없으면 빈 배열
    if (!trendData || typeof trendData !== 'object') {
      return [];
    }

    // 연구자수 차트 형식의 데이터를 series 형식으로 변환
    // TODO: API 응답 구조에 맞게 수정 필요
    const mapSeries = (label, color, arr) => {
      const rows = Array.isArray(arr) ? arr : [];
      const data = rows
        .filter(d => d && typeof d.indicator_year === 'number' && d.indicator_year > 0 && typeof d.count !== 'undefined' && d.count !== null && !isNaN(Number(d.count)))
        .map(d => ({ year: d.indicator_year, value: Number(d.count) }))
        .sort((a, b) => a.year - b.year);
      if (!data.length) return null;
      return { label, color, data };
    };

    // TODO: API 응답 구조에 맞게 수정 필요
    const sci = mapSeries('SCI', '#B298F2', trendData.SCI);
    const scopus = mapSeries('Scopus', '#FFBE8D', trendData.Scopus);
    const kci = mapSeries('KCI', '#5FA3E0', trendData.KCI);
    return [sci, scopus, kci].filter(Boolean);
  }, [externalSeries, trendData]);
  const ref = useRef(null);
  const tooltipRef = useRef(null);

  // 🔵 마지막 드로잉 상태 기억 (순수 JS 객체)
  const lastDrawRef = useRef({
    width: 0,
    height: 0,
    forcedYMax: null,
    hideXAxisLabels: false,
    showLegend: true,
    seriesSignature: '',
  });

  // Track container size reactively
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // 📏 레이아웃 측정 + ResizeObserver 디바운스
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return;

    let resizeTimeoutId;

    const measure = () => {
      const rect = el.getBoundingClientRect && el.getBoundingClientRect();
      const w = (rect && rect.width) || el.clientWidth || 0;
      const h = (rect && rect.height) || el.clientHeight || 0;

      setContainerSize(prev =>
        prev.width !== w || prev.height !== h ? { width: w, height: h } : prev,
      );
    };

    const scheduleMeasure = () => {
      if (resizeTimeoutId) {
        window.clearTimeout(resizeTimeoutId);
      }
      // 60ms 디바운스
      resizeTimeoutId = window.setTimeout(measure, 60);
    };

    // 초기 측정
    measure();

    let ro;

    try {
      ro = new ResizeObserver(() => {
        scheduleMeasure();
      });
      ro.observe(el);
    } catch (e) {
      // Fallback: window resize listener
      const onResize = () => scheduleMeasure();
      window.addEventListener('resize', onResize);
      const raf = requestAnimationFrame(measure);

      return () => {
        window.removeEventListener('resize', onResize);
        cancelAnimationFrame(raf);
        if (resizeTimeoutId) window.clearTimeout(resizeTimeoutId);
      };
    }

    // 폰트 로딩 이후 레이아웃 변동 대응
    try {
      if (document && document.fonts && typeof document.fonts.ready?.then === 'function') {
        document.fonts.ready.then(() => scheduleMeasure());
      }
    } catch (e) { }

    return () => {
      try {
        ro && ro.disconnect();
      } catch (e) { }
      if (resizeTimeoutId) window.clearTimeout(resizeTimeoutId);
    };
  }, []);

  const [hasDrawn, setHasDrawn] = useState(false);

  // 🎨 실제 그리는 useEffect (깜빡임/중복 렌더 방지)
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measuredWidth = containerSize.width;

    // 아직 레이아웃이 안 잡혔으면 그냥 이전 SVG 유지
    if (!measuredWidth || measuredWidth <= 0) {
      return;
    }

    const safeSeries = Array.isArray(series) ? series : [];

    // series 시그니처 (레이블 + year:value)
    const seriesSignature = safeSeries
      .map(s => {
        const key = s.key || s.label || '';
        const dataSig = (s.data || [])
          .map(d => `${d.year}:${d.value}`)
          .join(',');
        return `${key}::${dataSig}`;
      })
      .join('|');

    const last = lastDrawRef.current;
    const widthDelta = Math.abs((last.width || 0) - measuredWidth);
    const normalizedForcedYMax =
      typeof forcedYMax === 'number' && !Number.isNaN(forcedYMax) ? forcedYMax : null;

    // 대부분 동일하면 재그리기 스킵 (StrictMode/RO 버스트 방지)
    if (
      last.seriesSignature === seriesSignature &&
      widthDelta < 2 &&
      last.height === height &&
      last.forcedYMax === normalizedForcedYMax &&
      last.hideXAxisLabels === hideXAxisLabels &&
      last.showLegend === showLegend
    ) {
      return;
    }

    let raf1 = 0;
    let raf2 = 0;

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        // 실제로 다시 그릴 때만 지우기
        try {
          el.innerHTML = '';
        } catch (e) { }

        const rect = el.getBoundingClientRect && el.getBoundingClientRect();
        const widthNow = (rect && rect.width) || measuredWidth || 0;
        const width = widthNow || measuredWidth;
        const heightPx = height;
        const margin = { top: 16, right: 24, bottom: hideXAxisLabels ? 28 : 60, left: 44 };

        const svg = d3
          .select(el)
          .append('svg')
          .attr('width', '100%')
          .attr('height', heightPx)
          .attr('viewBox', `0 0 ${width} ${heightPx}`);

        const innerW = width - margin.left - margin.right;
        const innerH = heightPx - margin.top - margin.bottom;
        const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

        const years = Array.from(
          new Set(safeSeries.flatMap(s => (s.data || []).map(d => d.year))).values(),
        ).sort((a, b) => a - b);

        const allValues = safeSeries.flatMap(s =>
          (s.data || [])
            .map(d => d.value)
            .filter(v => typeof v === 'number' && !Number.isNaN(v)),
        );

        let yMin = 0;
        let yMaxData = allValues.length ? d3.max(allValues) : 10;
        let yMax = normalizedForcedYMax != null ? normalizedForcedYMax : yMaxData || 10;

        if (!Number.isFinite(yMin)) yMin = 0;
        if (!Number.isFinite(yMax)) yMax = 10;

        if (yMax === yMin) {
          if (yMax === 0) {
            yMax = 1;
          } else {
            yMin = yMin * 0.9;
            yMax = yMax * 1.1;
          }
        }

        const x = d3.scalePoint().domain(years).range([0, innerW]).padding(0.5);
        const y = d3
          .scaleLinear()
          .domain([yMin, yMax * 1.05])
          .nice()
          .range([innerH, 0]);

        // gridlines + y축
        g.append('g')
          .call(d3.axisLeft(y).ticks(8).tickSize(-innerW).tickFormat(d => `${d}`))
          .call(gAxis => {
            gAxis.selectAll('.domain').remove();
            gAxis.selectAll('line').attr('stroke', '#E4E8F1');
            gAxis.selectAll('text').attr('fill', '#8D9AAF').style('font-size', 12);
          });

        // x축
        const xAxis = g
          .append('g')
          .attr('transform', `translate(0,${innerH})`)
          .call(d3.axisBottom(x).tickSize(0).tickPadding(12));

        xAxis.call(gx => {
          gx.selectAll('.domain').remove();
          if (hideXAxisLabels) {
            gx.selectAll('text').remove();
            gx.selectAll('line').remove();
          } else {
            gx
              .selectAll('text')
              .attr('fill', '#6C7687')
              .style('font-size', 12);
          }
        });

        const line = d3
          .line()
          .x(d => x(d.year))
          .y(d => y(d.value))
          .curve(d3.curveMonotoneX);

        const tooltip = d3.select(tooltipRef.current);

        safeSeries.forEach(s => {
          const color = s.color || '#B298F2';
          const data = s.data || [];

          // path
          g.append('path')
            .datum(data)
            .attr('fill', 'none')
            .attr('stroke', color)
            .attr('stroke-width', 2.5)
            .attr('d', line);

          // points
          const pts = g
            .selectAll(`.pt-${s.key || s.label}`)
            .data(data)
            .enter()
            .append('g')
            .attr('transform', d => `translate(${x(d.year)},${y(d.value)})`);

          pts
            .append('circle')
            .attr('r', 4)
            .attr('fill', '#fff')
            .attr('stroke', color)
            .attr('stroke-width', 2);

          pts
            .append('circle')
            .attr('r', 10)
            .attr('fill', 'transparent')
            .on('mousemove', function (event, d) {
              tooltip
                .style('display', 'block')
                .style('left', `${margin.left + (x(d.year) || 0) - 40}px`)
                .style('top', `${margin.top + (y(d.value) || 0) - 36}px`)
                .html(
                  `<div class='px-2 py-1 text-[13px]'><span class='font-semibold text-[#2A2E36] mr-2'>${d.year}</span><span style='color:${color}' class='font-semibold mr-1'>${s.label}</span><span class='text-[#2A2E36]'>${d.value}</span></div>`,
                );
            })
            .on('mouseleave', () => tooltip.style('display', 'none'));
        });

        // 범례
        if (showLegend) {
          const items = safeSeries.map(s => ({ label: s.label, color: s.color }));
          if (items.length > 0) {
            const itemSpacing = 110;
            const legend = svg.append('g');

            items.forEach((it, i) => {
              const gItem = legend.append('g').attr('transform', `translate(${i * itemSpacing}, 0)`);

              gItem
                .append('circle')
                .attr('r', 6)
                .attr('fill', it.color || '#999');

              gItem
                .append('text')
                .attr('x', 10)
                .attr('y', 4)
                .attr('fill', '#6C7687')
                .style('font-size', 14)
                .text(it.label);
            });

            const legendNode = legend.node();
            if (legendNode && legendNode.getBBox) {
              const bbox = legendNode.getBBox();
              const offsetX = 12;
              const legendX = width / 2 - bbox.width / 2 + offsetX;
              const legendY = heightPx - 6;
              legend.attr('transform', `translate(${legendX}, ${legendY})`);
            }
          }
        }

        // 첫 렌더 이후 계속 true (빈 프레임 방지)
        setHasDrawn(true);

        // 실제 그린 상태 저장
        lastDrawRef.current = {
          width: measuredWidth,
          height,
          forcedYMax: normalizedForcedYMax,
          hideXAxisLabels,
          showLegend,
          seriesSignature,
        };
      });
    });

    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      // 여기서는 el.innerHTML 지우지 않음 → 깜빡임 방지
    };
  }, [series, height, forcedYMax, hideXAxisLabels, showLegend, containerSize.width]);

  const hasData = Array.isArray(series) && series.length > 0;
  const isLoading = externalLoading !== null ? externalLoading : (externalSeries === null && trendLoading);
  const error = externalError !== null ? externalError : (externalSeries === null && trendErr ? trendErr : null);

  return (
    <div className="relative min-h-[300px]">
      {/* 차트 컨테이너는 항상 렌더링 (ref가 필요하므로) */}
      <div ref={ref} className="w-full" style={{ opacity: hasDrawn ? 1 : 0 }} />
      {/* 로딩 상태 오버레이 - 로딩 중일 때만 표시 */}
      {isLoading && (
        <ChartSkeletonLoader show={true} />
      )}
      {/* 에러 상태 오버레이 - 로딩이 끝나고 에러가 있을 때만 표시 */}
      {!isLoading && error && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-[#F3652D] text-sm">
          {error}
        </div>
      )}
      {/* 데이터 없음 오버레이 - 로딩이 끝나고 에러가 없고 데이터가 없을 때만 표시 */}
      {!isLoading && !error && !hasData && (
        <div className="absolute inset-0 flex items-center justify-center text-[#8D9AAF]">
          데이터가 없습니다.
        </div>
      )}
      <div
        ref={tooltipRef}
        className="absolute z-10 hidden bg-white border border-[#E4E8F1] rounded-md shadow-sm"
        style={{ display: 'none' }}
      />
    </div>
  );
}

export default ResearcherCountChart;
